export interface ReferencePackDownloadCapture {
  id: string;
  imageUrl: string;
  format: string | null;
  capturedAt: string;
}

interface CaptureDownloadLabels {
  poseLabel?: string | null;
  cameraLabel?: string | null;
}

function toFilenamePart(value: string | null | undefined, fallback: string) {
  const normalized = value
    ?.normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return normalized || fallback;
}

function getCaptureDownloadTimestamp(capturedAt: string) {
  const date = new Date(capturedAt);
  if (Number.isNaN(date.getTime())) return 'undated';

  const iso = date.toISOString();
  return `${iso.slice(0, 10).replaceAll('-', '')}-${iso.slice(11, 19).replaceAll(':', '')}`;
}

export function getCaptureDownloadExtension(capture: ReferencePackDownloadCapture) {
  const format = capture.format?.trim().toLowerCase().replace(/^\./, '');
  const formatExtensions: Record<string, string> = {
    'image/jpeg': 'jpg',
    jpeg: 'jpg',
    jpg: 'jpg',
    'image/png': 'png',
    png: 'png',
    'image/webp': 'webp',
    webp: 'webp',
    'image/avif': 'avif',
    avif: 'avif',
  };

  if (format) {
    const knownExtension = formatExtensions[format];
    if (knownExtension) return knownExtension;

    const imageFormatMatch = format.match(/^image\/([a-z0-9.+-]+)$/);
    if (imageFormatMatch) {
      const extension = imageFormatMatch[1].split('+')[0];
      return extension === 'jpeg' ? 'jpg' : extension;
    }

    if (/^[a-z0-9]{2,5}$/.test(format)) {
      return format === 'jpeg' ? 'jpg' : format;
    }
  }

  const imagePath = getImageUrlPath(capture.imageUrl);
  const extensionMatch = imagePath.match(/\.([a-z0-9]{2,5})$/i);
  return extensionMatch?.[1]?.toLowerCase() || 'jpg';
}

export function getCaptureDownloadFilename(
  capture: ReferencePackDownloadCapture,
  labels: CaptureDownloadLabels
) {
  const posePart = toFilenamePart(labels.poseLabel, 'photo');
  const cameraPart = toFilenamePart(labels.cameraLabel, 'camera');
  const timestampPart = getCaptureDownloadTimestamp(capture.capturedAt);
  const idPart = capture.id.replace(/-/g, '').slice(0, 8) || 'capture';
  const extension = getCaptureDownloadExtension(capture);

  return `${posePart}-${cameraPart}-${timestampPart}-${idPart}.${extension}`;
}

function getImageUrlPath(imageUrl: string) {
  try {
    return new URL(imageUrl, 'https://reference-pack.local').pathname;
  } catch {
    return imageUrl.split(/[?#]/)[0] ?? '';
  }
}
